import React, { useMemo } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import OriginTracer from '@atlassiansox/origin-tracing/origin-tracer';
import LinkButton from '@atlaskit/button/link';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import { SpotlightPrimaryLink } from '@atlaskit/spotlight/primary-link';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import type { UpgradeButtonQuery } from './__generated__/UpgradeButtonQuery.graphql.ts';

const upgradeMessage = defineMessage({
	id: 'polaris__component-entitlements.upgrade-button.upgrade-message',
	defaultMessage: 'Upgrade',
	description:
		'The text is shown on a primary button that allows the user to upgrade their plan. Clicking this button initiates the upgrade flow.',
});

type Props = {
	appearance?: 'discovery' | 'spotlight';
	unavailablePlaceholder?: React.JSX.Element;
};

export const UpgradeButton = ({
	appearance = 'discovery',
	unavailablePlaceholder,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();

	const data = useLazyLoadQuery<UpgradeButtonQuery>(
		graphql`
			query UpgradeButtonQuery($cloudId: ID!) {
				tenantContexts(cloudIds: [$cloudId])
					@optIn(to: ["CcpAllUserUpgradeAndPay", "HamsAllUserUpgradeAndPay"]) {
					entitlementInfo(hamsProductKey: "jira-product-discovery") {
						entitlement {
							experienceCapabilities {
								... on CcpEntitlementExperienceCapabilities {
									changeOfferingV2(offeringName: "Premium") {
										experienceUrl
										isAvailableToUser
									}
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId },
	);

	const isAvailableToUser =
		data.tenantContexts?.[0]?.entitlementInfo?.entitlement?.experienceCapabilities?.changeOfferingV2
			?.isAvailableToUser ?? false;

	const experienceUrl =
		data.tenantContexts?.[0]?.entitlementInfo?.entitlement?.experienceCapabilities?.changeOfferingV2
			?.experienceUrl ?? null;

	const experienceUrlWithOrigin = useMemo(() => {
		if (!experienceUrl) {
			return null;
		}
		const origin = new OriginTracer({ product: 'jira' });
		return origin.addToUrl(experienceUrl);
	}, [experienceUrl]);

	if (!isAvailableToUser || !experienceUrlWithOrigin) {
		if (unavailablePlaceholder !== undefined) {
			return unavailablePlaceholder;
		}

		return null;
	}

	if (appearance === 'spotlight') {
		return (
			<SpotlightPrimaryLink href={experienceUrlWithOrigin} appearance="outline">
				{formatMessage(upgradeMessage)}
			</SpotlightPrimaryLink>
		);
	}

	return (
		<LinkButton href={experienceUrlWithOrigin} appearance="discovery" iconBefore={EditionsIcon}>
			{formatMessage(upgradeMessage)}
		</LinkButton>
	);
};
